import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';

/*
 * LiveStatusFooter — sticky bottom pill, the ONLY ticking element on the page.
 *
 * Receives dailyPanchang as a prop (per PO Risk 7 — no independent state).
 * Active period derivation:
 *  1. Check inauspicious periods first (Rahu Kaal, Yamagandam, Gulikai, Dur Muhurtam)
 *  2. Else check auspicious periods (Abhijit, Brahma, Amrit, Nalla Neram)
 *  3. Else neutral
 * Updates every 60s via the parent's `now` prop.
 * 200ms fade transition; instant under prefers-reduced-motion: reduce.
 */

const Pill = styled.aside`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${p => p.theme.colors.footer[p.tone]?.bg || 'transparent'};
  border-top: 1px solid ${p => p.theme.colors.border};
  padding: 10px 16px;
  font-size: 13px;
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};
  color: ${p => p.theme.colors.text.primary};
  text-align: center;
  z-index: 50;
  transition: background-color 0.2s ease, color 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  @media (max-width: ${p => p.theme.breakpoints.mobile}px) {
    padding: 12px 16px;
    min-height: 44px;
  }

  @media print {
    display: none;
  }
`;

const Dot = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${p => p.theme.colors.footer[p.tone]?.dot || '#9E9E9E'};
`;

function nowInRange(now, start, end) {
  if (!now || !start || !end) return false;
  const toMin = (t) => {
    const parts = t.split(':').map(Number);
    return (parts[0] || 0) * 60 + (parts[1] || 0);
  };
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const startMin = toMin(start);
  const endMin = toMin(end);
  if (endMin >= startMin) {
    return nowMin >= startMin && nowMin < endMin;
  }
  return nowMin >= startMin || nowMin < endMin;
}

function findActivePeriod(dailyPanchang, now) {
  if (!dailyPanchang || !now) return { tone: 'neutral' };

  const inauspicious = dailyPanchang.inauspicious || {};
  const checkSingle = (period, key, ta, en) => {
    if (period && nowInRange(now, period.start, period.end)) {
      return { tone: 'inauspicious', key, periodNameTa: ta, periodNameEn: en, untilTime: period.end };
    }
    return null;
  };

  const inResult =
    checkSingle(inauspicious.rahu_kaal, 'rahu', 'ராகு காலம்', 'Rahu Kaal') ||
    checkSingle(inauspicious.yamagandam, 'yama', 'யமகண்டம்', 'Yamagandam') ||
    checkSingle(inauspicious.gulikai, 'gulikai', 'குளிகை', 'Gulikai');
  if (inResult) return inResult;

  const dur = inauspicious.dur_muhurtam || inauspicious.durmuhurtam;
  if (Array.isArray(dur)) {
    for (const w of dur) {
      if (nowInRange(now, w.start, w.end)) {
        return { tone: 'inauspicious', key: 'dur', periodNameTa: 'துர் முகூர்த்தம்', periodNameEn: 'Dur Muhurtam', untilTime: w.end };
      }
    }
  } else if (dur) {
    const r = checkSingle(dur, 'dur', 'துர் முகூர்த்தம்', 'Dur Muhurtam');
    if (r) return r;
  }

  const auspicious = dailyPanchang.auspicious || {};
  const aResult =
    checkSingle(auspicious.abhijit_muhurta || auspicious.abhijit, 'abhijit', 'அபிஜித் முகூர்த்தம்', 'Abhijit Muhurta') ||
    checkSingle(auspicious.brahma_muhurta || auspicious.brahma, 'brahma', 'பிரம்ம முகூர்த்தம்', 'Brahma Muhurta') ||
    checkSingle(auspicious.amrit_kalam || auspicious.amritKalam, 'amrit', 'அமிர்த காலம்', 'Amrit Kalam');
  if (aResult) return { ...aResult, tone: 'auspicious' };

  const dayNalla = auspicious.nalla_neram_day || auspicious.nallaNeramDay;
  if (Array.isArray(dayNalla)) {
    for (const w of dayNalla) {
      if (nowInRange(now, w.start, w.end)) {
        return { tone: 'auspicious', key: 'nalla_d', periodNameTa: 'நல்ல நேரம்', periodNameEn: 'Nalla Neram', untilTime: w.end };
      }
    }
  }
  const nightNalla = auspicious.nalla_neram_night || auspicious.nallaNeramNight;
  if (Array.isArray(nightNalla)) {
    for (const w of nightNalla) {
      if (nowInRange(now, w.start, w.end)) {
        return { tone: 'auspicious', key: 'nalla_n', periodNameTa: 'நல்ல நேரம்', periodNameEn: 'Nalla Neram', untilTime: w.end };
      }
    }
  }

  return { tone: 'neutral' };
}

const LiveStatusFooter = ({ dailyPanchang, now, lang = 'en' }) => {
  const active = useMemo(() => findActivePeriod(dailyPanchang, now), [dailyPanchang, now]);

  let statusText;
  if (active.tone === 'neutral') {
    statusText = lang === 'ta' ? 'நடுநிலை நேரம்' : 'Neutral period';
  } else {
    const periodName = lang === 'ta' ? active.periodNameTa : active.periodNameEn;
    const untilStr = active.untilTime ? active.untilTime.substring(0, 5) : '';
    if (lang === 'ta') {
      statusText = `இப்போது ${periodName}${untilStr ? ` (${untilStr} வரை)` : ''}`;
    } else {
      statusText = `Now in ${periodName}${untilStr ? ` (until ${untilStr})` : ''}`;
    }
  }

  return (
    <Pill role="status" aria-live="polite" tone={active.tone}>
      <Dot tone={active.tone} aria-hidden="true" />
      <span>{statusText}</span>
    </Pill>
  );
};

export default LiveStatusFooter;
